import { GenderValues, FEM, MASC, NEU, COM } from "./Gender.js";
import { calculateHash } from "./utils/hash.js";
import { bincludes, consonants } from "./utils/alphabet.js";
import { toLowerCasePortable } from "./utils/letterCase.js";
import { last, takeLast } from "./utils/strings.js";
import { leftShift } from "./utils/decompress.js";

/**
 * The canonical form of a word.
 * Objects of this class also contain grammatical and semantic information,
 * allowing to choose inflection strategies and distinguish homonyms.
 *
 * Please use `Lemma.create`
 * or `Lemma.createOrNull` instead of the constructor.
 */
export class Lemma {

    /**
     * Please use the static methods create
     * and createOrNull instead of the constructor.
     */
    constructor(o) {
        if (o instanceof Lemma) {
            this._txt = o._txt;
            this._lc = o._lc;
            this._hash = o._hash;
            this._flags = o._flags;

        } else {
            if (o.pluraleTantum) {
                this._flags = 5;
            } else {
                this._flags = 1 + GenderValues.indexOf(o.gender);
            }

            this._txt = o.text;
            this._lc = toLowerCasePortable(o.text);
            this._hash = calculateHash(this._lc);

            this._flags |= (1 << 3) * (o.indeclinable&1);
            this._flags |= (1 << 4) * (o.animate&1);
            this._flags |= (1 << 5) * (o.surname&1);
            this._flags |= (1 << 6) * (o.name&1);
            this._flags |= (1 << 7) * (o.transport&1);

            this._flags |= leftShift(16) * (
                2 + calculateDeclension(this._lc, o.pluraleTantum, getIntGender(this), o.indeclinable)
            );
        }
    }

    /**
     * If the parameter is already a lemma, returns the same object, not a copy.
     *
     * @param {RussianNouns.Lemma|Object} o
     * @throws {Error} Errors from the lemma constructor.
     * @returns {RussianNouns.Lemma}
     */
    static create(o) {
        if (o instanceof this) {
            return o;
        }

        const err = validateCreateLemma(o);
        if (err) {
            throw new Error(err);
        }

        return Object.freeze(new this(o));
    }

    /**
     * Create a lemma with minimal overhead.
     *
     * @param {Object} options
     * @returns {RussianNouns.Lemma|null}
     */
    static createOrNull(options) {
        return (null === validateCreateLemma(options)) ?
                Object.freeze(new this(options)) :
                null;
    }

    equals(o) {
        return (o instanceof Lemma)
            && (this._flags === o._flags)
            && (this.lower() === o.lower());
    }

    text() {
        return this._txt;
    }

    lower() {
        return this._lc;
    }

    isPluraleTantum() {
        return 5 === (0b111 & this._flags);
    }

    getGender() {
        const i = getIntGender(this);
        if ((i >= 1) && (i <= 4)) {
            return GenderValues[i-1];
        }
    }

    isIndeclinable() {
        return ((1 << 3) & this._flags) !== 0;
    }

    isAnimate() {
        return (((1 << 4) & this._flags) !== 0) || this.isASurname() || this.isAName();
    }

    isASurname() {
        return ((1 << 5) & this._flags) !== 0;
    }

    isAName() {
        return ((1 << 6) & this._flags) !== 0;
    }

    isATransport() {
        return ((1 << 7) & this._flags) !== 0;
    }

    /**
     * Noun declension.
     *
     * Possible values:
     * + -1 — indeclinable, mostly loanwords;
     * + 0 — mixed declension ("путь" and "дитя");
     * + 1 — masculine and neuter without ending;
     * + 2 — words ending in "а", "я" (m., f. and common gender);
     * + 3 — feminine without ending; words ending in "мя".
     *
     * The concept of "declension" is hard to apply to plurale tantum words,
     * so this method returns -2 for them (instead of undefined).
     */
    getDeclension() {
        return (this._flags >> 16) - 2;
    }

    /**
     * Returns the "school" declension variant:
     * "вода" — first declension; "стол", "окно" — second declension.
     */
    getSchoolDeclension() {
        const d = this.getDeclension();

        if (d === 1) {
            return 2;
        } else if (d === 2) {
            return 1;
        } else {
            return d;
        }
    }
}


export function getIntGender(lemma) {
    return 0b111 & lemma._flags;
}


export function fastClone(lemma, newText) {
    const lemmaCopy = new Lemma(lemma);
    lemmaCopy._txt = newText;
    lemmaCopy._lc = toLowerCasePortable(newText);
    lemmaCopy._hash = calculateHash(lemmaCopy.lower());
    // Declension is not updated here because
    // everywhere I use this function, I already know
    // what declension will result.
    return Object.freeze(lemmaCopy);
}


/**
 * @param o A plain old JavaScript object.
 * @returns {string|null} Error description in English, or null.
 */
function validateCreateLemma(o) {
    if (null == o) {
        return 'No parameters specified.';
    }

    for (let fieldName of [
        'pluraleTantum',
        'indeclinable', 'animate',
        'surname', 'name', 'transport'
    ]) {
        const check = x => (null != x) && (typeof x != 'boolean');
        if (check(o[fieldName])) {
            return '' + fieldName + ' must be boolean.';
        }
    }

    if (o.text == null) {
        return 'A cyrillic word required.';
    }

    if (!o.pluraleTantum) {
        if (o.gender == null) {
            return 'A grammatical gender required.';
        }

        if (!GenderValues.includes(o.gender)) {
            return 'Bad grammatical gender.';
        }
    }

    return null;
}


function calculateDeclension(lcWord, pluraleTantum, gender, indeclinable) {
    if (pluraleTantum) {
        return -2; // undefined
    }

    if (indeclinable) {
        return -1;
    }

    const t = last(lcWord);
    switch (gender) {
        case FEM:
            return t === "а" || t === "я" ? 2 :
                bincludes(consonants, t) ? -1 : 3;

        case MASC:
            return t === "а" || t === "я" ? 2 :
                lcWord === "путь" ? 0 : 1;

        case NEU:
            return ['дитя', 'полудитя'].includes(lcWord) ? 0 :
                takeLast(lcWord, 2) === "мя" ? 3 : 1;

        case COM:
            if (t === 'а' || t === 'я') {
                return 2;
            } else if (t === 'и') {
                return -1;
            }
            return 1;

        default:
            return -2; // Error
    }
}


export function createLemma(o) {
    return Lemma.create(o);
}

export function createLemmaOrNull(o) {
    return Lemma.createOrNull(o);
}
